// Social impressions helper methods that format data to give to ImpressionWatcher.
// If you add a new source, make sure to update the spec here:`
// https://docs.google.com/document/d/1CbpPGacFCsmTAh1t29IEyUJQ-bJE15N4xagwm22M00k/edit#bookmark=id.dfnib8t49ryy

import cloneDeep from 'lodash/cloneDeep';
import round from 'lodash/round';
import omitBy from 'lodash/omitBy';
import toSnakecaseKeys from 'js/utils/toSnakecaseKeys';
import {
  NewsStory,
  NewsType,
  PaperType,
  VoteCountingRecommendationMetrics,
} from 'js/social/shared/types';
import { FeedPage } from 'js/social/Feed/hooks/useFeedConfig';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { Props as ImpressionProps } from 'js/utils/impressions/ImpressionWatcher';

export function getImpressionPage(page: FeedPage) {
  return {
    home: 'home',
    profile: 'prof',
    single_post: '', // We're currently not logging anything on the single post page
    hashtag: 'hash',
    upload: '', // We're not currently logging anything on the upload page.
    research_interest: 'ri', // We're not currently logging anything on the research_interest page.
    liswp: '', // We're not logging anything on the LISWP
  }[page];
}

export function getImpressionPaperType(type: PaperType | null) {
  if (type) {
    return {
      certified_work: 'rec',
      paper_upload: 'upload',
      bookmark: 'saved',
      attached: 'attach',
    }[type];
  }
  return 'default';
}

export function getImpressionFeedExtraData({
  newsType,
  newsId,
  feedIndex,
  feedReason,
  recommendationScore,
  recommendationMetrics,
  extendedOnsiteRecsTestBucket,
  recencyWeightedTestBucket,
  guessTheResearcherTestBucket,
  newsStory,
  topicGroupId,
  topicGroupName,
  topicGroupSources,
  topicGroupActorCount,
  impression_tracking_id,
  imageId,
  videoId,
}: {
  newsType: NewsType;
  newsId: string | number;
  feedIndex?: number;
  feedReason?: string;
  recommendationScore?: number;
  recommendationMetrics?: VoteCountingRecommendationMetrics;
  extendedOnsiteRecsTestBucket?: string;
  recencyWeightedTestBucket?: string;
  guessTheResearcherTestBucket?: string;
  newsStory?: NewsStory;
  topicGroupId?: number;
  topicGroupName?: string;
  topicGroupSources?: string[];
  topicGroupActorCount?: number;
  impression_tracking_id?: string;
  imageId?: string | number;
  videoId?: string | number;
}) {
  const source_table =
    newsType === 'post'
      ? 'social_posts'
      : newsType === 'paper_update'
        ? 'social_paper_updates'
        : newsStory // 'work' and 'openalex_work' news types that come with a story
          ? 'llm_news_stories'
          : newsType === 'openalex_work'
            ? 'openalex_works'
            : newsType === 'work'
              ? 'works'
              : newsType === 'paper_set'
                ? 'paper_sets'
                : undefined;

  const source_id = newsStory ? newsStory.id : newsId;

  let data: Record<string, string | number | undefined> = {};

  if (feedIndex !== undefined && feedReason !== undefined) {
    data = {
      feed_index: feedIndex,
      feed_reason: feedReason,
      source_table,
      source_id: source_id,
      extended_onsite_recs_test_bucket: extendedOnsiteRecsTestBucket,
      recency_weighted_test_bucket: recencyWeightedTestBucket,
      guess_the_researcher_test_bucket: guessTheResearcherTestBucket,
      topic_group_id: topicGroupId,
      topic_group_name: topicGroupName,
      topic_group_sources: topicGroupSources?.join(','),
      topic_group_actor_count: topicGroupActorCount,
      recommendation_score: undefined,
      recommendation_metrics: undefined,
      impression_tracking_id: impression_tracking_id,
      image_id: imageId,
      video_id: videoId,
    };

    if (recommendationScore !== undefined) {
      data.recommendation_score = recommendationScore;
    } else {
      data.recommendation_metrics = toSnakecaseKeys(recommendationMetrics);
    }
  }

  return omitBy(data, (value) => value === '' || value == null);
}

/**
 * Format the extra data column, to be used by impressions on the suggestions tab of network page.
 * @param category suggestion category, equivalent to the name of the source subclass. Defined in
 * app/lib/suggestions/user_group_suggester.rb#source_subclasses_to_consider
 * @param categoryIndex 0-indexed position of the category on the suggestions tab
 * @param cardIndex 0-indexed position of the suggestion card within the category
 * @returns a hash of the extra data to be logged
 */
export function getImpressionNetworkExtraData(
  category: string,
  categoryIndex: number,
  cardIndex: number,
) {
  return {
    category: category,
    category_index: categoryIndex,
    card_index: cardIndex,
  };
}

/**
 * Impression sources are capped at 64 characters. If the generated source is longer than that,
 * either the inputs are wrong or the source spec needs to be updated to meet this requirement.
 */
export function getSocialImpressionSource(
  page: string,
  section: string,
  element?: string,
  paperType?: string,
  clickTarget?: string,
): string {
  let source = `soc_${page}_${section}`;
  if (element != null) {
    source += `_${element}`;
  }
  if (paperType != null) {
    if (element == null) {
      console.error(
        'Impression source has paperType but no element. This will cause invalid impressions.',
      );
    }
    source += `_${paperType}`;
  }
  if (clickTarget != null) {
    if (element == null || paperType == null) {
      console.error(
        'Impression source has clickTarget but either no element or no paperType. This will cause invalid impressions.',
      );
    }
    source += `_${clickTarget}`;
  }
  return source.toLowerCase();
}

// This is a curried function which generates the props needed for a given
// <ImpressionWatcher>. It is curried to allow higher up components to fill in
// information before passing down to lower level presentational components. It
// is referenced as `getImpressionProps` in multiple places, even when some of
// the information has been filled in.
export type ImpressionPropsGenerator = (
  // If no entity is passed in, the work for the news feed item is used
  entityType?: ImpressionEntity,
  entityId?: number | string,
) => (
  // If no element is passed in, the main element for the feed unit (i.e.
  // paper/oa-work/post) will be used
  element?: string,
) => (clickTarget?: string) => ImpressionProps;

export type ImpressionPropsGeneratorWithEntity =
  ReturnType<ImpressionPropsGenerator>;
export type ImpressionPropsGeneratorWithEntityAndElement =
  ReturnType<ImpressionPropsGeneratorWithEntity>;

// Given some default values for the source and entity, creates an
// ImpressionPropsGenerator
export function makeImpressionPropsGenerator(
  page: string,
  section: string,
  defaultElement: string,
  paperType: string,
  defaultEntityType: ImpressionEntity,
  defaultEntityId: number | string,
  extra?: object,
  trackDimensions?: boolean,
): ImpressionPropsGenerator {
  return (entityType?: ImpressionEntity, entityId?: number | string) =>
    (element?: string) =>
    (clickTarget?: string) => {
      const newsItemImpressionSource = getSocialImpressionSource(
        page,
        section,
        element ?? defaultElement,
        paperType,
        clickTarget,
      );

      const providedEntity = entityType != null && entityId != null;

      return {
        source: newsItemImpressionSource,
        entity_type: providedEntity ? entityType : defaultEntityType,
        entity_id: providedEntity ? entityId : defaultEntityId,
        extra,
        trackDimensions,
      };
    };
}

/*
 * User suggestion scoring metadata serialization/deserialization
 */

type Schema = {
  [key: string]:
    | string
    | {
        alias: string;
        nested: Schema;
      };
};

const USER_SUGGESTION_SCORING_METADATA_SOURCE_ALIASES = {
  TopResearchInterestUsers: 'triu',
  DepartmentColleagues: 'dc',
  UniversityColleagues: 'uc',
  ImportedContacts: 'ic',
  OwnFollowers: 'of',
  YouMayKnow: 'ymk',
  AuthorsBookmarked: 'ab',
  SimilarActivity: 'sa',
  FollowedBySimilarBookmarkers: 'fbsb',
  FollowedByFollowees: 'fbf',
  FollowedBySimilarFollowers: 'fbsf',
};

const USER_SUGGESTION_SCORING_METADATA_GENERAL_BOOST_ALIASES = {
  works: 'w',
  has_photo: 'hp',
  followers_count: 'fc',
  days_since_last_seen: 'dsls',
  is_boosted_reverse_contact: 'ibrc',
};

const USER_SUGGESTION_SCORING_METADATA_SCHEMA: Schema = {
  source_scores: {
    alias: 'ss',
    nested: USER_SUGGESTION_SCORING_METADATA_SOURCE_ALIASES,
  },
  source_boosted_scores: {
    alias: 'sbs',
    nested: USER_SUGGESTION_SCORING_METADATA_SOURCE_ALIASES,
  },
  general_boost_parameters: {
    alias: 'gbp',
    nested: USER_SUGGESTION_SCORING_METADATA_GENERAL_BOOST_ALIASES,
  },
  general_boost_components: {
    alias: 'gbc',
    nested: USER_SUGGESTION_SCORING_METADATA_GENERAL_BOOST_ALIASES,
  },
  general_boost: 'gb',
  total: 't',
};

/**
 * Utility for transforming user suggestion scoring metadata to fit within the 512 character limit of the `extra` column
 * of the impression table.
 *
 * Converts strings and object keys to their aliases, defined above.
 * Truncates numbers to whole number, or four significant digits, whichever is more precise.
 * @param data
 */
export function serializeUserSuggestionScoringMetadata(data?: {
  [key: string]: any;
}) {
  return userSuggestionScoringMetadataSerializationHelper(
    data,
    USER_SUGGESTION_SCORING_METADATA_SCHEMA,
  );
}

function userSuggestionScoringMetadataSerializationHelper(
  data: any,
  schema: Schema = {},
): any {
  if (data == null) return;

  if (typeof data === 'boolean') return data ? 1 : undefined;

  if (typeof data === 'number') {
    if (data === 0) return;
    if (Math.abs(data) >= 1000) return round(data);

    return round(data, 3 - Math.floor(Math.log10(Math.abs(data))));
  }

  // return alias if it exists, otherwise return original value
  if (typeof data === 'string') {
    const alias = subSchemaHelper(schema[data])?.alias;
    return alias || data;
  }

  if (Array.isArray(data))
    return data.map((element) => {
      return userSuggestionScoringMetadataSerializationHelper(element, schema);
    });

  // handle objects
  const output = {} as { [key: string]: any };
  for (const key in data) {
    const value = data[key];
    const maybeSubSchema = subSchemaHelper(schema[key]);
    if (!maybeSubSchema) {
      output[key] = cloneDeep(value);
    } else {
      output[maybeSubSchema.alias] =
        userSuggestionScoringMetadataSerializationHelper(
          value,
          maybeSubSchema.nested,
        );
    }
  }

  return output;
}

function subSchemaHelper(
  aliasOrSubSchema: string | { alias: string; nested: Schema },
): { alias: string; nested: Schema } | undefined {
  if (!aliasOrSubSchema) return;
  if (typeof aliasOrSubSchema === 'string')
    return { alias: aliasOrSubSchema, nested: {} };

  return aliasOrSubSchema;
}
